import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import './Contact.css';

const Contact: React.FC = () => {
    const [ref, inView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        company: '',
        project: '',
        budget: '',
        message: '',
    });

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            const response = await fetch('https://formspree.io/f/xkgvpvrz', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: formData.name,
                    email: formData.email,
                    phone: formData.phone,
                    company: formData.company,
                    project: formData.project,
                    budget: formData.budget,
                    message: formData.message,
                    _replyto: formData.email,
                    _subject: `Novo orçamento - ${formData.name} - ${formData.project}`
                }),
            });

            if (response.ok) {
                alert('Obrigado! Entraremos em contato em até 2 horas!');
                // Limpar formulário
                setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    company: '',
                    project: '',
                    budget: '',
                    message: '',
                });
            } else {
                throw new Error('Erro no envio');
            }
        } catch (error) {
            alert('Erro ao enviar formulário. Tente novamente ou entre em contato via WhatsApp.');
            console.error('Erro:', error);
        }
    };

    const whatsappMessage = `Olá! Gostaria de solicitar um orçamento para desenvolvimento de landing page/site institucional/catálogo digital.`;
    const whatsappUrl = `https://wa.me/5528999921986?text=${encodeURIComponent(whatsappMessage)}`;

    return (
        <section id="contato" className="contact section">
            <div className="container">
                <motion.div
                    ref={ref}
                    initial={{ opacity: 0, y: 50 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.8 }}
                    className="contact-header"
                >
                    <h2 className="section-title">
                        Transforme Seu Negócio Hoje
                    </h2>
                    <p className="section-subtitle">
                        Não deixe seus concorrentes saírem na frente. Solicite seu orçamento agora
                        e fortaleça sua presença online ainda hoje!
                    </p>
                </motion.div>

                <div className="contact-content">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={inView ? { opacity: 1, x: 0 } : {}}
                        transition={{ duration: 0.8, delay: 0.3 }}
                        className="contact-info"
                    >
                        <div className="urgency-banner">
                            <h3>🔥 Oferta Limitada!</h3>
                            <p>Primeiros 10 clientes ganham:</p>
                            <ul>
                                <li>✅ Hospedagem grátis por 1 mês</li>
                                <li>✅ Certificado SSL incluso</li>
                                <li>✅ 1 mês de suporte gratuito</li>
                                <li>✅ Otimização SEO para leads</li>
                            </ul>
                            <div className="countdown">
                                <span>⏰ Restam apenas 3 vagas!</span>
                            </div>
                        </div>

                        <div className="contact-methods">
                            <h4>Fale Conosco Agora:</h4>

                            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="contact-method whatsapp">
                                <div className="method-icon">📱</div>
                                <div className="method-info">
                                    <h5>WhatsApp</h5>
                                    <p>Resposta em até 5 minutos</p>
                                    <span className="method-number">(28) 99992-1986</span>
                                </div>
                            </a>

                            <a href="mailto:contato@genesystecnologia.com.br" className="contact-method email">
                                <div className="method-icon">📧</div>
                                <div className="method-info">
                                    <h5>E-mail</h5>
                                    <p>Resposta em até 2 horas</p>
                                    <span className="method-number">contato@genesystecnologia.com.br</span>
                                </div>
                            </a>

                            <div className="contact-method phone">
                                <div className="method-icon">📞</div>
                                <div className="method-info">
                                    <h5>Telefone</h5>
                                    <p>Seg-Sex: 8h às 18h</p>
                                    <span className="method-number">(28) 99992-1986</span>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        animate={inView ? { opacity: 1, x: 0 } : {}}
                        transition={{ duration: 0.8, delay: 0.5 }}
                        className="contact-form-container"
                    >
                        <form onSubmit={handleSubmit} className="contact-form">
                            <h3>Solicite Seu Orçamento Gratuito</h3>

                            <div className="form-row">
                                <div className="form-group">
                                    <input
                                        type="text"
                                        name="name"
                                        placeholder="Seu nome completo"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <input
                                        type="email"
                                        name="email"
                                        placeholder="Seu melhor e-mail"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <input
                                        type="tel"
                                        name="phone"
                                        placeholder="WhatsApp com DDD"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <input
                                        type="text"
                                        name="company"
                                        placeholder="Nome da empresa"
                                        value={formData.company}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <select
                                        name="project"
                                        value={formData.project}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Tipo de projeto</option>
                                        <option value="site-institucional">Site Institucional</option>
                                        <option value="landing-page">Landing Page</option>
                                        <option value="catalogo-digital">Catálogo Digital</option>
                                        <option value="sistema-web">Sistema Web</option>
                                        <option value="redesign">Redesign</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <select
                                        name="budget"
                                        value={formData.budget}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Orçamento disponível</option>
                                        <option value="ate-2k">Até R$ 2.000</option>
                                        <option value="2k-5k">R$ 2.000 - R$ 5.000</option>
                                        <option value="5k-10k">R$ 5.000 - R$ 10.000</option>
                                        <option value="10k-mais">Acima de R$ 10.000</option>
                                    </select>
                                </div>
                            </div>

                            <div className="form-group">
                                <textarea
                                    name="message"
                                    placeholder="Conte-nos sobre seu projeto e objetivos..."
                                    rows={4}
                                    value={formData.message}
                                    onChange={handleInputChange}
                                ></textarea>
                            </div>

                            <button type="submit" className="btn-primary btn-large">
                                🚀 Solicitar Orçamento Gratuito
                            </button>

                            <p className="form-disclaimer">
                                * Responderemos em até 2 horas úteis com uma proposta personalizada
                            </p>
                        </form>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default Contact;